import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ReadableCard } from "../Sticker";
import ClassesByVenueList from "../ClassesByVenueList";
import {
  FieldHint,
  PUBLIC_LISTING_CARD_CLASS,
  SectionTitle,
} from "../ui/FormSection";
import {
  CLASS_PREVIEW,
  EVENT_PREVIEW,
  classListingHref,
  classListingMeta,
  classListingTitle,
  eventHref,
  eventListingMeta,
  splitNextEvent,
} from "../../lib/publicListingFormat";
import { listingDrawerState } from "../../lib/listingDrawer";
import NextEventFlyer from "./NextEventFlyer";

const ROW_HOVER_CLASS =
  "transition-colors hover:bg-black/[0.03] focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2";

const ROW_LINK_CLASS =
  `flex items-start justify-between gap-3 min-h-[44px] rounded-btn -mx-1 px-1 ${ROW_HOVER_CLASS}`;

const COLLAPSED_SECTION_CLASS =
  `w-full min-h-[44px] flex items-center gap-2 font-ui font-medium text-[15px] text-ink rounded-btn -mx-1 px-1 ${ROW_HOVER_CLASS}`;

const SHOW_MORE_CLASS =
  "mt-2 font-ui font-medium text-[13px] text-ink-secondary min-h-[44px] rounded-btn px-1 -ml-1 transition-colors hover:text-ink focus-visible:outline focus-visible:outline-2 focus-visible:outline-orchid/45 focus-visible:outline-offset-2";

export function ListingSection({
  title,
  testId,
  empty,
  children,
  items,
  footer = null,
  listWrap = true,
  hint = null,
}) {
  return (
    <ReadableCard className={PUBLIC_LISTING_CARD_CLASS} data-testid={testId}>
      <SectionTitle>{title}</SectionTitle>
      {hint}
      {items?.length ? (
        <>
          {listWrap ? (
            <ul className="pt-1 divide-y divide-black/[0.08]">{children}</ul>
          ) : (
            <div className="pt-1">{children}</div>
          )}
          {footer}
        </>
      ) : (
        <FieldHint className="pt-1">{empty}</FieldHint>
      )}
    </ReadableCard>
  );
}

export function ListingRow({ to, state, testId, title, meta, actionLabel = "RSVP →" }) {
  const body = (
    <>
      <div className="min-w-0">
        <div className="font-ui font-medium text-[14px] text-ink">{title}</div>
        {meta && (
          <div className="text-[13px] text-ink-secondary mt-0.5 font-ui font-medium">{meta}</div>
        )}
      </div>
      {to ? (
        <span className="inline-flex min-h-[44px] items-center px-1 text-[13px] font-medium text-ink-secondary shrink-0 underline decoration-2 underline-offset-[4px] decoration-ink/40">
          {actionLabel}
        </span>
      ) : null}
    </>
  );
  return (
    <li className="py-3 first:pt-2">
      {to ? (
        <Link to={to} state={state} className={ROW_LINK_CLASS} data-testid={testId}>
          {body}
        </Link>
      ) : (
        <div className="flex items-start justify-between gap-3 min-h-[44px] -mx-1 px-1" data-testid={testId}>
          {body}
        </div>
      )}
    </li>
  );
}

export function ShowMoreButton({ count, expanded, onExpand, onCollapse, testId }) {
  if (expanded) {
    return (
      <button
        type="button"
        onClick={onCollapse}
        className={SHOW_MORE_CLASS}
        data-testid={`${testId}-show-less`}
      >
        Show fewer
      </button>
    );
  }
  if (count <= 0) return null;
  return (
    <button
      type="button"
      onClick={onExpand}
      className={SHOW_MORE_CLASS}
      data-testid={testId}
    >
      Show {count} more
    </button>
  );
}

/** @deprecated Prefer NextEventFlyer — kept as alias for any external imports. */
export function NextEventCard(props) {
  return <NextEventFlyer {...props} />;
}

export function EmptyUpcomingCard({ name, testIdPrefix }) {
  return (
    <ReadableCard className={PUBLIC_LISTING_CARD_CLASS} data-testid={`${testIdPrefix}-empty`}>
      <SectionTitle>Upcoming</SectionTitle>
      <FieldHint className="pt-1">
        Quiet on the board
        {name ? ` — follow ${name} so you catch the next night` : ". Check back soon"}.
      </FieldHint>
    </ReadableCard>
  );
}

function ClassesListingSection({
  classes,
  venues,
  headerSchoolName,
  showAll,
  onExpand,
  onCollapse,
  testIdPrefix,
  hint = null,
  previewCount = CLASS_PREVIEW,
  startCollapsed = false,
  linkState = undefined,
}) {
  const [sectionOpen, setSectionOpen] = useState(!startCollapsed);
  const visible = showAll ? classes : classes.slice(0, previewCount);

  if (startCollapsed && !sectionOpen) {
    return (
      <ReadableCard className={PUBLIC_LISTING_CARD_CLASS} data-testid={`${testIdPrefix}-classes`}>
        <button
          type="button"
          onClick={() => setSectionOpen(true)}
          className={COLLAPSED_SECTION_CLASS}
          data-testid={`${testIdPrefix}-classes-expand`}
          aria-expanded={false}
          aria-label={`Show ${classes.length} classes`}
        >
          <span>Classes</span>
          <span className="font-ui font-medium text-[13px] text-ink-secondary tabular-nums" aria-hidden>
            · {classes.length}
          </span>
        </button>
      </ReadableCard>
    );
  }

  return (
    <ListingSection
      title="Classes"
      testId={`${testIdPrefix}-classes`}
      items={classes}
      listWrap={false}
      empty="No upcoming classes yet."
      hint={hint}
      footer={
        <>
          {classes.length > previewCount ? (
            <ShowMoreButton
              count={classes.length - previewCount}
              expanded={showAll}
              onExpand={onExpand}
              onCollapse={onCollapse}
              testId={`${testIdPrefix}-classes-show-more`}
            />
          ) : null}
          {startCollapsed ? (
            <button
              type="button"
              onClick={() => {
                setSectionOpen(false);
                onCollapse();
              }}
              className={SHOW_MORE_CLASS}
              data-testid={`${testIdPrefix}-classes-collapse`}
              aria-expanded={true}
            >
              Hide classes
            </button>
          ) : null}
        </>
      }
    >
      <ClassesByVenueList
        instances={visible}
        venues={venues}
        listClassName="divide-y divide-black/[0.08]"
        groupedListClassName="divide-y divide-black/[0.08]"
        renderRow={(listing) => {
          const classId = listing.class_id || listing.id;
          return (
            <ListingRow
              key={classId}
              to={classListingHref(listing)}
              state={linkState}
              testId={`${testIdPrefix}-class-${classId}`}
              title={classListingTitle(listing)}
              meta={classListingMeta(listing, headerSchoolName)}
            />
          );
        }}
      />
    </ListingSection>
  );
}

function EventsListingSection({
  listedEvents,
  nextEvent,
  showAll,
  onExpand,
  onCollapse,
  testIdPrefix,
  startCollapsed = false,
  eventTitleFn = null,
  linkState = undefined,
}) {
  const [sectionOpen, setSectionOpen] = useState(!startCollapsed);

  if (!listedEvents.length) return null;
  const title = nextEvent && listedEvents.length ? "More events" : "Events";
  const visible = showAll ? listedEvents : listedEvents.slice(0, EVENT_PREVIEW);

  if (startCollapsed && !sectionOpen) {
    return (
      <ReadableCard className={PUBLIC_LISTING_CARD_CLASS} data-testid={`${testIdPrefix}-events`}>
        <button
          type="button"
          onClick={() => setSectionOpen(true)}
          className={COLLAPSED_SECTION_CLASS}
          data-testid={`${testIdPrefix}-events-expand`}
          aria-expanded={false}
          aria-label={`Show ${listedEvents.length} more events`}
        >
          <span>{title}</span>
          <span className="font-ui font-medium text-[13px] text-ink-secondary tabular-nums" aria-hidden>
            · {listedEvents.length}
          </span>
        </button>
      </ReadableCard>
    );
  }

  return (
    <ListingSection
      title={title}
      testId={`${testIdPrefix}-events`}
      items={listedEvents}
      empty="No upcoming events yet."
      footer={
        <>
          {listedEvents.length > EVENT_PREVIEW ? (
            <ShowMoreButton
              count={listedEvents.length - EVENT_PREVIEW}
              expanded={showAll}
              onExpand={onExpand}
              onCollapse={onCollapse}
              testId={`${testIdPrefix}-events-show-more`}
            />
          ) : null}
          {startCollapsed ? (
            <button
              type="button"
              onClick={() => {
                setSectionOpen(false);
                onCollapse();
              }}
              className={SHOW_MORE_CLASS}
              data-testid={`${testIdPrefix}-events-collapse`}
              aria-expanded={true}
            >
              Hide events
            </button>
          ) : null}
        </>
      }
    >
      {visible.map((ev) => (
        <ListingRow
          key={`${ev.id}-${ev.occurrence_date || ev.occurrence_at || ev.starts_at}`}
          to={eventHref(ev)}
          state={linkState}
          testId={`${testIdPrefix}-event-${ev.id}`}
          title={eventTitleFn ? eventTitleFn(ev) : ev.name}
          meta={eventListingMeta(ev)}
        />
      ))}
    </ListingSection>
  );
}

/**
 * Shared classes / events listings for host + school public profiles.
 * Classes are one row per weekly class (schedule_label + next instance link).
 * Events are one row per series (recurring_label + next occurrence link).
 * @param {"host" | "school"} sectionOrder
 * @param {React.ReactNode} afterClasses — school: team (or other) after classes
 * @param {boolean} promoteNextEvent — false when parent renders its own next-event flyer
 * @param {(ev: object) => string} [eventTitleFn] — optional host title cleaner; school omits
 */
export default function PublicListingsBody({
  classes = [],
  events = [],
  venues,
  headerSchoolName = null,
  emptyName = null,
  sectionOrder = "host",
  testIdPrefix = "host-profile",
  classesHint = null,
  afterClasses = null,
  promoteNextEvent = true,
  eventTitleFn = null,
  hasExternalNextEvent = false,
  /** When true, open event/class in a Medium right drawer (host/school public pages). */
  drawerLinks = false,
}) {
  const location = useLocation();
  const linkState = drawerLinks ? listingDrawerState(location) : undefined;
  const [showAllClasses, setShowAllClasses] = useState(false);
  const [showAllEvents, setShowAllEvents] = useState(false);
  const { nextEvent, listedEvents } = promoteNextEvent
    ? splitNextEvent(events)
    : { nextEvent: null, listedEvents: events };
  const hasUpcoming = Boolean(
    nextEvent || hasExternalNextEvent || listedEvents.length || classes.length,
  );
  const schoolLayout = sectionOrder === "school";

  const classesSection =
    classes.length > 0 ? (
      <ClassesListingSection
        key="classes"
        classes={classes}
        venues={venues}
        headerSchoolName={headerSchoolName}
        showAll={showAllClasses}
        onExpand={() => setShowAllClasses(true)}
        onCollapse={() => setShowAllClasses(false)}
        testIdPrefix={testIdPrefix}
        hint={classesHint}
        previewCount={schoolLayout ? 3 : CLASS_PREVIEW}
        startCollapsed={!schoolLayout}
        linkState={linkState}
      />
    ) : null;

  const nextEventSection = nextEvent ? (
    <NextEventFlyer
      key="next-event"
      event={nextEvent}
      testIdPrefix={testIdPrefix}
      linkState={linkState}
    />
  ) : null;

  const eventsSection = (
    <EventsListingSection
      key="events"
      listedEvents={listedEvents}
      nextEvent={nextEvent || (hasExternalNextEvent ? true : null)}
      showAll={showAllEvents}
      onExpand={() => setShowAllEvents(true)}
      onCollapse={() => setShowAllEvents(false)}
      testIdPrefix={testIdPrefix}
      startCollapsed={schoolLayout}
      eventTitleFn={eventTitleFn}
      linkState={linkState}
    />
  );

  const afterClassesSection =
    afterClasses != null ? (
      <React.Fragment key="after-classes">{afterClasses}</React.Fragment>
    ) : null;

  const ordered = schoolLayout
    ? [nextEventSection, eventsSection, classesSection, afterClassesSection]
    : [nextEventSection, eventsSection, classesSection];

  return (
    <>
      {!hasUpcoming && (
        <EmptyUpcomingCard name={emptyName} testIdPrefix={testIdPrefix} />
      )}
      {ordered}
    </>
  );
}

